/**
 * SceneTreeNode — single domain-agnostic row for the Scene Tree V2.
 *
 * Renders one `FlatRow` (node variant) as a `role="treeitem"` element.
 * All domain differences flow through lookup tables (`ICON_MAP`,
 * `DOMAIN_TOKEN_MAP`) — zero domain branches in this file.
 *
 * Anatomy (left-to-right per 50-ui-design-brief.md §5.1):
 *   indent | chevron (40px) | icon | label | LoR chip | domain badge | visibility toggle
 *
 * Accessibility: ARIA 1.2 Tree View pattern per 50-ui-design-brief.md §9.
 * Keyboard: Enter → select/expand, Space → toggle visibility.
 * Arrow key handling is delegated to the parent `SceneTreeV2`.
 *
 * Styling: all values come from `tokens.*` or CSS custom properties
 * (placeholder domain-colour tokens). No raw hex/px/em.
 */

import type { CSSProperties, KeyboardEvent } from "react";
import { tokens } from "@/design";
import type { ControlOverlayEntry } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { NodeFlatRow } from "./treeProjection";
import { getIconForNode } from "./iconMap";
import { DOMAIN_TOKEN_MAP, tokenAliasToCssVar } from "./domainTokenMap";

// ─────────────────────────────────────────────────────────────────────────────
// Prop types
// ─────────────────────────────────────────────────────────────────────────────

export interface SceneTreeNodeProps {
  readonly row: NodeFlatRow;
  readonly selected: boolean;
  readonly onToggleExpand: (nodeId: string) => void;
  readonly onSelect: (nodeId: string) => void;
  readonly onToggleVisibility?: (nodeId: string) => void;
  /**
   * Block 2 — the control-overlay entry for this node (visible / isolated /
   * highlighted / colorOverride / opacity). When present, the row reflects it:
   * dimmed when not visible, accent background when highlighted, an isolation
   * marker when isolated, and a colour swatch for `colorOverride`. Absent /
   * undefined means "no overlay" (default appearance).
   */
  readonly overlay?: ControlOverlayEntry;
  /**
   * When set, the eye icon reflects catalog mount-gate visibility
   * (`visibleLayerIds`) instead of the control-overlay default (visible).
   */
  readonly catalogLayerVisible?: boolean;
  /** P3 — opacity change handler. */
  readonly onOpacityChange?: (objectId: string, value: number) => void;
  /** P3 — context menu handler. */
  readonly onContextMenu?: (e: React.MouseEvent, nodeId: string) => void;
  /** Zoom-to handler — renders an inline "zoom to layer" button when provided. */
  readonly onZoomTo?: (nodeId: string) => void;
  /** Opens the right-rail layer settings/properties card for this row. */
  readonly onOpenSettings?: (nodeId: string) => void;
  /** P3 — active search query for match highlighting. */
  readonly searchQuery?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components / helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Renders a Material Symbols icon using the font-ligature approach. */
function MaterialIcon({
  name,
  style,
  "aria-hidden": ariaHidden = true,
}: {
  name: string;
  style?: CSSProperties;
  "aria-hidden"?: boolean;
}) {
  return (
    <span
      aria-hidden={ariaHidden}
      style={{
        fontFamily: "Material Symbols Rounded",
        fontSize: tokens.font.size.md,
        lineHeight: 1,
        userSelect: "none",
        ...style,
      }}
    >
      {name}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function SceneTreeNode({
  row,
  selected,
  onToggleExpand,
  onSelect,
  onToggleVisibility,
  overlay,
  catalogLayerVisible,
  onContextMenu,
  onZoomTo,
  onOpenSettings,
  searchQuery,
}: SceneTreeNodeProps) {
  const { node, depth, hasChildren, isExpanded } = row;

  const domainEntry = DOMAIN_TOKEN_MAP[node.domain];
  const domainBgColor = tokenAliasToCssVar(
    domainEntry.tokenAlias,
    tokens.color.surface[3],
  );
  const domainFgColor = tokenAliasToCssVar(
    `${domainEntry.tokenAlias}-on`,
    tokens.color.ink.secondary,
  );

  const iconName = getIconForNode(node);

  // ── Control-overlay flags (Block 2) ─────────────────────────────────────────
  // Catalog layer rows mirror `visibleLayerIds` (mount gate). Other rows use the
  // overlay; `visible !== false` defaults to visible when no entry exists.
  const isHidden =
    typeof catalogLayerVisible === "boolean"
      ? !catalogLayerVisible
      : overlay?.visible === false;
  const isHighlighted = overlay?.highlighted === true;
  const isIsolated = overlay?.isolated === true;
  const colorOverride = overlay?.colorOverride;
  // Explicit per-object opacity (0–1) dims the row content; hidden nodes keep
  // full text readability (only the eye icon changes to visibility_off).
  const contentOpacity = isHidden
    ? 0.7
    : typeof overlay?.opacity === "number"
      ? Math.max(0, Math.min(1, overlay.opacity))
      : 1;

  // ── Row styles ─────────────────────────────────────────────────────────────

  const compactControlSize = tokens.density.rowDense - tokens.space[1];
  const compactIconSize = tokens.font.size.lg;
  const compactMetaColumnWidth = tokens.layout.collapsedRail * 5 - tokens.space[1];
  const compactIndentStep = tokens.space[4] - tokens.border.width.thin * 2;
  const compactChipFontSize = tokens.font.size.xs - tokens.border.width.thin;

  const actionColumns = [
    onZoomTo ? `${compactControlSize}px` : null,
    onToggleVisibility ? `${compactControlSize}px` : null,
    onOpenSettings && node.layerId ? `${compactControlSize}px` : null,
    onContextMenu ? `${compactControlSize}px` : null,
  ].filter(Boolean).join(" ");

  const rowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `${compactControlSize}px ${compactIconSize}px minmax(0, 1fr) minmax(0, ${compactMetaColumnWidth}px)${actionColumns ? ` ${actionColumns}` : ""}`,
    alignItems: "center",
    minHeight: tokens.density.rowDense,
    margin: `${tokens.border.width.thin}px ${tokens.space[1]}px`,
    paddingLeft: depth * compactIndentStep + tokens.space[1],
    paddingRight: tokens.space[1],
    gap: tokens.space[1],
    cursor: "pointer",
    userSelect: "none",
    position: "relative",
    // Precedence: explicit highlight > selected > transparent.
    backgroundColor: isHighlighted
      ? tokenAliasToCssVar("color.accent.subtle", tokens.color.accent.subtle)
      : selected
        ? tokens.color.surface[2]
        : "transparent",
    borderLeft: selected
      ? `${tokens.border.width.thin * 3}px solid ${tokens.color.accent.primary}`
      : isHighlighted
        ? `${tokens.border.width.thin * 3}px solid ${tokens.color.accent.strong}`
        : `${tokens.border.width.thin * 3}px solid transparent`,
    borderRadius: tokens.density.rowDense / 2,
    // Dim the whole row when the object is hidden or has reduced opacity.
    opacity: contentOpacity,
    outline: "none",
    // Transition for hover/focus state layers
    transition: `background-color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}, opacity ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}`,
  };

  const compactIconButtonStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: compactControlSize,
    height: compactControlSize,
    minWidth: compactControlSize,
    // Match V1 / chip controls — full-circle + accent fill looked like a
    // selected state and made visible vs hidden eyes feel inconsistent.
    borderRadius: tokens.radius.input,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: tokens.color.ink.secondary,
  };

  const compactChipStyle: CSSProperties = {
    fontSize: compactChipFontSize,
    fontWeight: tokens.font.weight.medium,
    borderRadius: tokens.radius.chip,
    padding: `${tokens.border.width.thin * 2}px ${tokens.space[1]}px`,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  // ── Event handlers ─────────────────────────────────────────────────────────

  function handleChevronClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (hasChildren) onToggleExpand(node.id);
  }

  function handleRowClick() {
    onSelect(node.id);
  }

  function handleRowContextMenu(e: React.MouseEvent) {
    onContextMenu?.(e, node.id);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      onSelect(node.id);
      if (hasChildren) onToggleExpand(node.id);
    } else if (e.key === " ") {
      e.preventDefault();
      onToggleVisibility?.(node.id);
    }
    // Arrow keys are handled by the parent tree for roving tabindex.
  }

  function handleVisibilityClick(e: React.MouseEvent) {
    e.stopPropagation();
    onToggleVisibility?.(node.id);
  }

  function handleZoomClick(e: React.MouseEvent) {
    e.stopPropagation();
    onZoomTo?.(node.id);
  }

  function handleMenuButtonClick(e: React.MouseEvent) {
    e.stopPropagation();
    // Reuse the right-click context-menu path; the click event carries
    // clientX/clientY so the menu opens anchored to the button.
    onContextMenu?.(e, node.id);
  }

  function handleSettingsClick(e: React.MouseEvent) {
    e.stopPropagation();
    onOpenSettings?.(node.id);
  }

  // ── ARIA attrs ─────────────────────────────────────────────────────────────

  const ariaExpandedAttr =
    hasChildren ? { "aria-expanded": isExpanded } : {};

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      role="treeitem"
      aria-selected={selected}
      aria-level={depth + 1}
      {...ariaExpandedAttr}
      tabIndex={selected ? 0 : -1}
      data-node-id={node.id}
      data-overlay-hidden={isHidden ? "true" : undefined}
      data-overlay-highlighted={isHighlighted ? "true" : undefined}
      data-overlay-isolated={isIsolated ? "true" : undefined}
      data-overlay-color={colorOverride ?? undefined}
      style={rowStyle}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
      onContextMenu={handleRowContextMenu}
    >
      {/* Chevron — 40px touch target */}
      <button
        aria-label={
          isExpanded ? `Collapse ${node.label}` : `Expand ${node.label}`
        }
        onClick={handleChevronClick}
        tabIndex={-1}
        style={{
          ...compactIconButtonStyle,
          cursor: hasChildren ? "pointer" : "default",
          color: hasChildren
            ? tokens.color.ink.secondary
            : "transparent",
          transition: `transform ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}`,
          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
        }}
      >
        {hasChildren && (
          <MaterialIcon name="chevron_right" />
        )}
      </button>

      {/* Node icon */}
      <MaterialIcon
        name={iconName}
        style={{
          color: selected
            ? tokens.color.ink.primary
            : tokens.color.ink.secondary,
          fontSize: tokens.space[4],
          justifySelf: "center",
        }}
      />

      {/* Label — shows the short name with optional search highlighting */}
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: tokens.font.size.sm,
          fontWeight: selected ? tokens.font.weight.semibold : tokens.font.weight.medium,
          lineHeight: tokens.font.lineHeight.tight,
          color: selected ? tokens.color.ink.primary : tokens.color.ink.primary,
          fontFamily: tokens.font.family.ui,
        }}
        title={node.label}
      >
        {renderHighlightedLabel(
          node.label.includes(".")
            ? node.label.slice(node.label.lastIndexOf(".") + 1)
            : node.label,
          searchQuery,
        )}
      </span>

      {/* Compact metadata slot keeps action icons aligned across every row. */}
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 4,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {node.designation && (node.designation.functional || node.designation.product || node.designation.location) && (
        <span
          aria-label={[
            node.designation.functional,
            node.designation.product,
            node.designation.location,
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            fontSize: tokens.font.size.xs,
            fontFamily: tokens.font.family.mono ?? "monospace",
            color: tokens.color.ink.muted,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: tokens.space[8] - tokens.space[3],
          }}
        >
          {[
            node.designation.functional,
            node.designation.product,
            node.designation.location,
          ]
            .filter(Boolean)
            .join(" ")}
        </span>
        )}

        {/* Control-overlay: colour-override swatch (Block 2) */}
        {colorOverride && (
        <span
          role="img"
          aria-label={`Colour override ${colorOverride}`}
          data-testid="overlay-color-swatch"
          style={{
            width: tokens.space[2],
            height: tokens.space[2],
            flexShrink: 0,
            borderRadius: tokens.radius.sm,
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            background: tokenAliasToCssVar(colorOverride, tokens.color.accent.primary),
          }}
        />
        )}

        {/* Control-overlay: isolation marker (Block 2) */}
        {isIsolated && (
        <span
          role="img"
          aria-label="Isolated"
          data-testid="overlay-isolated-marker"
          style={{ display: "inline-flex", flexShrink: 0 }}
        >
          <MaterialIcon
            name="center_focus_strong"
            style={{
              color: tokens.color.accent.primary,
              fontSize: tokens.font.size.sm,
            }}
          />
        </span>
        )}

        {/* LoR chip */}
        <span
        aria-label={`Level of Resolution ${node.lor}`}
        style={{
          ...compactChipStyle,
          color: tokens.color.ink.muted,
          background: tokens.color.surface[2],
        }}
      >
        {`LoR${node.lor}`}
        </span>

        {/* Domain badge */}
        <span
        aria-label={domainEntry.ariaLabel}
        data-domain={node.domain}
        style={{
          ...compactChipStyle,
          background: domainBgColor,
          color: domainFgColor,
          maxWidth: tokens.space[7] + tokens.space[2],
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {domainEntry.label}
        </span>

        {/* Readiness chip (Task 4) — green/yellow/red based on metadata.readiness */}
        {!!node.metadata?.readiness && (
        <span
          aria-label={`Readiness: ${node.metadata.readiness as string}`}
          data-testid="readiness-chip"
          style={{
            width: tokens.space[2],
            height: tokens.space[2],
            flexShrink: 0,
            borderRadius: "50%",
            background: readinessColor(node.metadata.readiness as string),
          }}
        />
        )}

        {/* Scope-marker badges (Phase 5) — _DEM, _REL, etc. per 119 §12 */}
        {Array.isArray(node.metadata?.scopeMarkers) &&
        (node.metadata.scopeMarkers as string[]).map((marker: string) => (
          <span
            key={marker}
            aria-label={`Scope marker: ${marker}`}
            data-testid="scope-marker-chip"
            style={{
              ...compactChipStyle,
              color: tokens.color.ink.muted,
              background: tokens.color.surface[3],
              fontFamily: tokens.font.family.mono ?? "monospace",
              letterSpacing: "0.02em",
            }}
          >
            {marker}
          </span>
        ))}
      </span>

      {/* Zoom-to — inline button, always visible on the row when wired. */}
      {onZoomTo && (
        <button
          aria-label={`Zoom to layer ${node.label}`}
          title="Zoom to layer"
          onClick={handleZoomClick}
          tabIndex={-1}
          style={{
            ...compactIconButtonStyle,
            color: tokens.color.ink.secondary,
          }}
        >
          <MaterialIcon name="my_location" style={{ fontSize: tokens.space[4] }} />
        </button>
      )}

      {/* Visibility toggle — same chrome for every row (leaf + folder). */}
      {onToggleVisibility && (
        <button
          aria-label={isHidden ? `Show ${node.label}` : `Hide ${node.label}`}
          aria-pressed={!isHidden}
          title={isHidden ? "Show layer" : "Hide layer"}
          onClick={handleVisibilityClick}
          tabIndex={-1}
          data-visibility={isHidden ? "hidden" : "visible"}
          style={{
            ...compactIconButtonStyle,
            color: isHidden ? tokens.color.ink.muted : tokens.color.accent.primary,
            // Visible = accent icon only (no filled circle). Hidden = muted
            // visibility_off. Hover affordance comes from the row, not a
            // second “selected” chip that only appears on some layers.
            background: "transparent",
          }}
        >
          <MaterialIcon name={isHidden ? "visibility_off" : "visibility"} style={{ fontSize: tokens.space[4] }} />
        </button>
      )}

      {/* Layer settings/properties — opens the right rail for CRS/source/bounds. */}
      {onOpenSettings && node.layerId && (
        <button
          aria-label={`Open settings for ${node.label}`}
          title="Layer settings"
          onClick={handleSettingsClick}
          tabIndex={-1}
          style={{
            ...compactIconButtonStyle,
            color: tokens.color.ink.secondary,
          }}
        >
          <MaterialIcon name="settings" style={{ fontSize: tokens.space[4] }} />
        </button>
      )}

      {/* Context-menu (3-dot) — opens the same menu as right-click. */}
      {onContextMenu && (
        <button
          aria-label={`Actions for ${node.label}`}
          aria-haspopup="menu"
          title="More actions"
          onClick={handleMenuButtonClick}
          tabIndex={-1}
          style={{
            ...compactIconButtonStyle,
            color: tokens.color.ink.secondary,
          }}
        >
          <MaterialIcon name="more_vert" style={{ fontSize: tokens.space[4] }} />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function readinessColor(readiness: string): string {
  switch (readiness) {
    case "ready":
    case "green":
      return tokens.color.semantic.success;
    case "partial":
    case "yellow":
      return tokens.color.semantic.warning;
    case "not-ready":
    case "red":
      return tokens.color.semantic.danger;
    default:
      return tokens.color.ink.muted;
  }
}

function renderHighlightedLabel(
  label: string,
  query?: string,
): JSX.Element | string {
  if (!query?.trim()) return label;
  const q = query.trim();
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark
        style={{
          background: tokens.color.accent.subtle,
          color: "inherit",
          borderRadius: 2,
          padding: 0,
        }}
      >
        {label.slice(idx, idx + q.length)}
      </mark>
      {label.slice(idx + q.length)}
    </>
  );
}
